
import { useState, useEffect, useRef } from "react";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { useToast } from "../../hooks/use-toast";
import debounce from "lodash/debounce";
import { cn } from "../../lib/utils";

interface EditableFieldProps {
  value: string;
  onChange: (value: string) => Promise<void> | void;
  type?: "text" | "textarea" | "title";
  placeholder?: string;
  showToast?: boolean;
  className?: string;
}

export function EditableField({
  value,
  onChange,
  type = "text",
  placeholder = "",
  showToast = false,
  className,
}: EditableFieldProps) {
  const [localValue, setLocalValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const debouncedSave = useRef(
    debounce(async (newValue: string) => {
      try {
        await onChange(newValue);
        if (showToast) {
          toast({
            title: "Changes saved",
            description: "Your changes have been saved automatically.",
          });
        }
      } catch (error) {
        console.error("Error saving changes:", error);
        toast({
          title: "Error saving changes",
          description: "Please try again.",
          variant: "destructive",
        });
      }
    }, 1000)
  ).current;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  useEffect(() => {
    if (type === "textarea" && inputRef.current instanceof HTMLTextAreaElement) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [localValue, type]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | React.FormEvent<HTMLDivElement>
  ) => {
    const target = e.target as HTMLElement;
    const newValue = target.innerText || (target as HTMLInputElement).value;
    setLocalValue(newValue);
    debouncedSave(newValue);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>
  ) => {
    if (e.key === "Enter" && !e.shiftKey && type !== "textarea" && type !== "title") {
      e.preventDefault();
      const target = e.target as HTMLElement;
      target.blur();
    }
  };

  if (type === "title") {
    return (
      <div
        ref={inputRef as React.RefObject<HTMLDivElement>}
        contentEditable
        onInput={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setIsEditing(false)}
        onFocus={() => setIsEditing(true)}
        className={cn(
          "outline-none focus:outline-none whitespace-pre-wrap break-words hover:bg-muted/50 rounded-lg p-4",
          className
        )}
        suppressContentEditableWarning={true}
        dangerouslySetInnerHTML={{ __html: localValue || placeholder }}
      />
    );
  }

  if (type === "textarea") {
    return (
      <Textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={cn(
          "w-full resize-none bg-transparent hover:bg-muted/50 focus:bg-background border-none focus:ring-0 p-4 rounded-lg",
          !localValue && "min-h-[120px]",
          className
        )}
      />
    );
  }

  return (
    <Input
      ref={inputRef as React.RefObject<HTMLInputElement>}
      value={localValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={cn(
        "w-full bg-transparent hover:bg-muted/50 focus:bg-background border-none focus:ring-0 p-4 rounded-lg",
        className
      )}
    />
  );
}
